/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import Loader from 'components/atoms/loader';
import Pagination from 'components/atoms/pagination';
import { useTaskDetail } from 'hooks/useTaskDetail';
import { taskStateOf } from 'services/ai_management_task_state';
import { deleteTask } from 'services/actions/ai_management_tasks';
import AiManagementTaskDetail from 'components/molecules/ai_management_task_detail';
import AiManagementTaskNew from 'components/molecules/ai_management_task_new';
import { BiddingData, BiddingTaskTypes } from 'store/modules/bidding_store/types';
import { formatDateTimeLong } from 'services/ai_management_dates';
import { Container } from './styles';

const PAGE_SIZE = 5;

const formatDeadline = (deadline: string | null) =>
  deadline ? formatDateTimeLong(deadline) : 'Sem prazo';

interface AiManagementBiddingTasksProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
  patch: (updater: (current: BiddingData) => BiddingData) => void;
  openNewTask?: boolean;
  initialTaskId?: number;
}

const AiManagementBiddingTasks: React.FC<AiManagementBiddingTasksProps> = ({
  bidding,
  loading,
  error,
  patch,
  openNewTask = false,
  initialTaskId,
}) => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [openTaskId, setOpenTaskId] = useState<number | null>(initialTaskId ?? null);
  const [creating, setCreating] = useState(!!openNewTask);

  const tasks = useMemo(
    () => [...(bidding?.tasks ?? [])].sort((a, b) => a.position - b.position),
    [bidding],
  );

  const openTask = tasks.find((task) => task.id === openTaskId) ?? null;
  const {
    task: openTaskDetail,
    loading: loadingTaskDetail,
    error: taskDetailError,
    replace: replaceTaskDetail,
  } = useTaskDetail(openTaskId);

  const totalPages = Math.max(1, Math.ceil(tasks.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageTasks = tasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageIds = pageTasks.map((task) => task.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleOpen = (id: number) => {
    setCreating(false);
    setOpenTaskId((current) => (current === id ? null : id));
  };

  const startCreating = () => {
    setOpenTaskId(null);
    setCreating(true);
  };

  const handleCreated = (task: BiddingTaskTypes) => {
    patch((current) => ({ ...current, tasks: [...(current.tasks ?? []), task] }));
    setCreating(false);
  };

  const handleCreatedMany = (created: BiddingTaskTypes[]) => {
    patch((current) => ({ ...current, tasks: [...(current.tasks ?? []), ...created] }));
    setCreating(false);
  };

  const handleSaved = (updated: BiddingTaskTypes) => {
    replaceTaskDetail(updated);
    patch((current) => ({
      ...current,
      tasks: (current.tasks ?? []).map((item) => (item.id === updated.id ? updated : item)),
    }));
  };

  const handleDelete = (target: BiddingTaskTypes) => {
    if (!window.confirm(`Excluir a tarefa "${target.title}"?`)) return;
    deleteTask(target.id).then((res) => {
      if (!res || res.status >= 400) return;
      setOpenTaskId(null);
      patch((current) => ({
        ...current,
        tasks: (current.tasks ?? []).filter((item) => item.id !== target.id),
      }));
    });
  };

  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Loader />
      </div>
    );
  }

  if (error || !bidding) {
    return (
      <div className="alert alert-warning m-4" role="alert">
        Não foi possível carregar as tarefas.
      </div>
    );
  }

  return (
    <Container className="d-flex">
      <div className="tasks-list flex-grow-1 overflow-auto p-4">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <h4 className="mb-0">Tarefas</h4>
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-primary btn-sm d-flex align-items-center mr-2"
              onClick={startCreating}
            >
              <i className="material-icons mr-1">add</i>
              Adicionar tarefa
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center"
              disabled={tasks.length === 0}
            >
              <i className="material-icons mr-1">filter_list</i>
              Filtros
            </button>
          </div>
        </div>

        {tasks.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
            <label className="mb-0 d-flex align-items-center" htmlFor="select-all-tasks">
              <input
                id="select-all-tasks"
                type="checkbox"
                className="mr-2"
                checked={allPageSelected}
                onChange={toggleAllPage}
                disabled={pageIds.length === 0}
              />
              Selecionar todas as tarefas da página
            </label>

            <div className="d-flex">
              <button
                type="button"
                className="btn btn-secondary btn-sm mr-2"
                disabled={selected.size === 0}
              >
                Ações
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm mr-2"
                disabled={selected.size === 0}
              >
                Compartilhar
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={selected.size === 0}
              >
                Exportar
              </button>
            </div>
          </div>
        )}

        {tasks.length === 0 && (
          <div className="tasks-empty bg-white border rounded text-center mx-auto mt-5 p-4">
            <p className="tasks-empty-title font-weight-bold text-dark mb-1">
              Você e a sua equipe ainda não possuem tarefas para essa licitação
            </p>
            <p className="text-muted mb-0">
              Comece agora mesmo{' '}
              <button
                type="button"
                className="btn btn-link p-0 tasks-empty-link"
                onClick={startCreating}
              >
                adicionando uma nova tarefa
              </button>
            </p>
          </div>
        )}

        {pageTasks.map((task) => {
          const { label, badge } = taskStateOf(task);
          return (
            <div
              key={task.id}
              className={`task-row card border p-3 mb-2 d-flex flex-row ${
                openTaskId === task.id ? 'task-row-active' : ''
              }`}
              role="button"
              tabIndex={0}
              aria-expanded={openTaskId === task.id}
              aria-label={`Abrir ${task.title}`}
              onClick={() => toggleOpen(task.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleOpen(task.id);
                }
              }}
            >
              <input
                type="checkbox"
                className="mt-1 mr-3 flex-shrink-0"
                checked={selected.has(task.id)}
                onChange={() => toggleOne(task.id)}
                onClick={(event) => event.stopPropagation()}
                aria-label={`Selecionar ${task.title}`}
              />

              <div className="flex-grow-1 min-width-0">
                <div className="d-flex align-items-center flex-wrap mb-2">
                  <span className={`badge badge-${badge} mr-2`}>{label}</span>
                  <span className="task-title font-weight-bold">{task.title}</span>
                </div>

                <div className="d-flex align-items-center flex-wrap text-muted small">
                  <span className="d-flex align-items-center mr-3">
                    <i className="material-icons mr-1">event</i>
                    {formatDeadline(task.deadline)}
                  </span>
                  {task.responsible && (
                    <span className="d-flex align-items-center mr-3">
                      <i className="material-icons mr-1">person</i>
                      {task.responsible.name}
                    </span>
                  )}
                  <span className="task-count border rounded d-flex align-items-center mr-2">
                    <i className="material-icons mr-1">attach_file</i>
                    {task.counts.attachments}
                  </span>
                  <span className="task-count border rounded d-flex align-items-center">
                    <i className="material-icons mr-1">chat_bubble_outline</i>
                    {task.counts.comments}
                  </span>
                </div>
              </div>

              <span
                className={`btn btn-sm align-self-center d-flex align-items-center ml-3 ${
                  openTaskId === task.id ? 'btn-success' : 'btn-secondary'
                }`}
                aria-hidden="true"
              >
                <i className="material-icons">chevron_right</i>
              </span>
            </div>
          );
        })}

        {totalPages > 1 && (
          <Pagination
            postsPerPage={PAGE_SIZE}
            totalPosts={tasks.length}
            currentPage={currentPage}
            paginate={setPage}
            firstAndLastPage
          />
        )}
      </div>

      {creating && (
        <AiManagementTaskNew
          biddingId={Number(bidding.bidding_id) || Number(bidding.id)}
          onClose={() => setCreating(false)}
          onCreated={handleCreated}
          onCreatedMany={handleCreatedMany}
        />
      )}

      {!creating && openTask && (
        <aside className="task-pane flex-shrink-0 border-left bg-white d-flex flex-column">
          <div className="task-pane-header d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
            <h4 className="mb-0">Tarefa</h4>
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="task-pane-delete btn btn-link p-0 d-flex align-items-center mr-3"
                aria-label="Excluir tarefa"
                onClick={() => handleDelete(openTask)}
              >
                <i className="material-icons">delete_outline</i>
              </button>
              <button
                type="button"
                className="btn btn-link text-dark p-0 d-flex align-items-center"
                aria-label="Fechar tarefa"
                onClick={() => setOpenTaskId(null)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
          </div>
          {loadingTaskDetail && (
            <div className="py-5">
              <Loader />
            </div>
          )}

          {taskDetailError && (
            <div className="alert alert-warning m-4" role="alert">
              Não foi possível carregar a tarefa.
            </div>
          )}

          {openTaskDetail && <AiManagementTaskDetail task={openTaskDetail} onSaved={handleSaved} />}
        </aside>
      )}
    </Container>
  );
};

export default AiManagementBiddingTasks;
